import React, { useState, useEffect, useMemo } from "react";
import { ClipLoader } from "react-spinners";
import { Card, CardHeader, CardBody, Row, Col } from "reactstrap";
import moment from "moment";
import api from "../../../../../services/api";
import { useTheme } from "../../../../../hooks/theme";
import { useCompanies } from "../../../../../hooks/companies";
import LogCard from "../../../../../components/LogCard/LogCard";
import LogDetailsModal from "../../../../../components/LogDetailsModal/LogDetailsModal";

// PO1-2715 — card de log de NF barrada na importação (irmão de ChartLogCTe).
const DynamicCards = () => {
  const { darkMode } = useTheme();
  const { empresa, transportadora, arrayParams } = useCompanies();

  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalDetails, setModalDetails] = useState([]);
  const [selectedLogType, setSelectedLogType] = useState("");
  const auxDtInicial = useMemo(
    () => moment().subtract(90, "days").startOf("month"),
    []
  );
  const auxDtFinal = useMemo(() => moment().endOf("month"), []);

  // Memoriza os objetos para que sua referência seja estável
  const logTypeNames = useMemo(
    () => ({
      transportadoraNaoCadastrada: "Transportadora não cadastrada",
      outros: "Outros Erros",
    }),
    []
  );

  const logTypeDescriptions = useMemo(
    () => ({
      transportadoraNaoCadastrada:
        "NF barrada: o CNPJ da Transportadora informado na NF não está cadastrado no sistema.",
      outros: "Erros diversos não categorizados.",
    }),
    []
  );

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        setError(false);

        const params = {
          dataInicial: auxDtInicial.format("YYYY-MM-DD 00:00:00"),
          dataFinal: auxDtFinal.format("YYYY-MM-DD 23:59:59"),
        };

        if (arrayParams.embarcadores) {
          params.embarcadores = arrayParams.embarcadores;
        }

        if (arrayParams.transportadoras) {
          params.transportadoras = arrayParams.transportadoras;
        }

        const response = await api.get("/dashboard/cockpit/lognf", { params });

        if (response.status === 200) {
          const rawData = response.data;
          const maxCount = Math.max(
            ...Object.values(rawData).map((item) => item.quantidade)
          );
          const formattedData = Object.entries(rawData).map(([key, value]) => ({
            key, // Adiciona o identificador original
            type: logTypeNames[key] || key, // Nome amigável
            count: value.quantidade,
            percentage: (value.quantidade / maxCount) * 100, // Baseado no valor máximo
            description: logTypeDescriptions[key] || "Descrição não disponível", // Descrição
          }));
          setLogs(formattedData);
        }
      } catch (error) {
        console.error("Erro ao carregar os logs de NF:", error);
        setError(true);
      } finally {
        setLoading(false);
      }
    }

    if (empresa) {
      fetchData();
    }
  }, [
    empresa,
    transportadora,
    arrayParams.embarcadores,
    arrayParams.transportadoras,
    auxDtInicial,
    auxDtFinal,
    logTypeNames,
    logTypeDescriptions,
  ]);

  const fetchLogDetails = async (logType) => {
    try {
      const params = {
        dataInicial: auxDtInicial.format("YYYY-MM-DD 00:00:00"),
        dataFinal: auxDtFinal.format("YYYY-MM-DD 23:59:59"),
        tipoLog: logType,
      };

      const response = await api.get("/dashboard/cockpit/lognf/details", {
        params,
      });

      if (response.status === 200) {
        setModalDetails(response.data);
        setSelectedLogType(logTypeNames[logType] || logType);
        setModalOpen(true);
      }
    } catch (error) {
      console.error("Erro ao buscar os detalhes dos logs de NF:", error);
    }
  };

  const toggleModal = () => setModalOpen(!modalOpen);

  return (
    <>
      <Card className="card-chart">
        <CardHeader
          style={{
            backgroundColor: darkMode ? "#27293c" : "#ffffff",
          }}
        >
          <Row style={{ alignItems: "center" }}>
            <Col>
              <h4 style={{ marginBottom: "5px" }}>Resumo de Logs de NF</h4>
              <p
                style={{
                  fontSize: "0.9rem",
                  color: darkMode ? "#cfcfcf" : "#666666",
                  marginBottom: "20px",
                }}
              >
                Monitoramento das NFs barradas na importação.
              </p>
            </Col>
          </Row>
        </CardHeader>
        <CardBody style={{ paddingLeft: "2rem", paddingRight: "2rem" }}>
          {loading ? (
            <div style={{ textAlign: "center" }}>
              <ClipLoader size={90} color="#1d8cf8" loading={loading} />
              <h4 style={{ marginTop: "20px", color: "#999999" }}>
                Buscando dados...
              </h4>
            </div>
          ) : error ? (
            <div style={{ textAlign: "center", color: "red" }}>
              <h5>Ocorreu um erro ao buscar os dados.</h5>
            </div>
          ) : logs.length > 0 ? (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "15px",
                justifyContent: "space-between",
              }}
            >
              {logs.map((log) => (
                <LogCard
                  key={log.key}
                  type={log.type}
                  count={log.count}
                  percentage={log.percentage}
                  darkMode={darkMode}
                  description={log.description}
                  onClick={() => fetchLogDetails(log.key)}
                />
              ))}
            </div>
          ) : (
            <div style={{ textAlign: "center" }}>
              <h5>Nenhum dado encontrado!</h5>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Usando o componente LogDetailsModal */}
      <LogDetailsModal
        isOpen={modalOpen}
        toggle={toggleModal}
        title={`Detalhes do Log: ${selectedLogType}`}
        data={modalDetails}
      />
    </>
  );
};

export default DynamicCards;
